<script lang="ts">
  /**
   * Settings' "Controls": the two handling timings, and the keys.
   *
   * A page of its own (the shell's `FullPage`), laid over Settings rather
   * than a view swapped inside it: it gets its own history entry, so Back,
   * Escape and Done each step out exactly one level and leave Settings open
   * underneath (`docs/decisions/0047`).
   *
   * The steppers (the shell's `Stepper`) write straight to `settings`, which
   * a run in progress reads live (`play/play-input.svelte.ts`).
   */
  import { FullPage, Group, Row, SectionLabel, Stepper } from '@artibens/app-shell';

  import { KEY_HINTS } from '../copy/keys.js';
  import { persistSettings, settings } from '../state/settings.svelte.js';

  const { open, onclose }: { open: boolean; onclose: () => void } = $props();

  const DAS_STEP_MS = 10;
  const ARR_STEP_MS = 2;
  const DAS_MIN_MS = 10;
  const ARR_MIN_MS = 0;

  function stepDas(delta: number): void {
    settings.dasMs = Math.max(DAS_MIN_MS, settings.dasMs + delta);
    persistSettings();
  }

  function stepArr(delta: number): void {
    settings.arrMs = Math.max(ARR_MIN_MS, settings.arrMs + delta);
    persistSettings();
  }
</script>

{#snippet dasStepper()}
  <Stepper
    label="DAS"
    value={`${String(settings.dasMs)}ms`}
    decreaseLabel="Decrease DAS"
    increaseLabel="Increase DAS"
    canDecrease={settings.dasMs > DAS_MIN_MS}
    testids={{ value: 'das-value' }}
    ondecrease={() => {
      stepDas(-DAS_STEP_MS);
    }}
    onincrease={() => {
      stepDas(DAS_STEP_MS);
    }}
  />
{/snippet}

{#snippet arrStepper()}
  <Stepper
    label="ARR"
    value={`${String(settings.arrMs)}ms`}
    decreaseLabel="Decrease ARR"
    increaseLabel="Increase ARR"
    canDecrease={settings.arrMs > ARR_MIN_MS}
    testids={{ value: 'arr-value' }}
    ondecrease={() => {
      stepArr(-ARR_STEP_MS);
    }}
    onincrease={() => {
      stepArr(ARR_STEP_MS);
    }}
  />
{/snippet}

<FullPage {open} title="Controls" wide {onclose} testid="controls-sheet">
  <Group name="Handling">
    <Row
      label="DAS"
      below="How long a held direction waits before it repeats."
      trailing={dasStepper}
    />
    <Row label="ARR" below="How fast it repeats once it has started." trailing={arrStepper} />
  </Group>

  <SectionLabel as="h3" variant="sheet">Keyboard</SectionLabel>
  <Group name="Keyboard">
    {#each KEY_HINTS as row (row.action)}
      <Row label={row.action} value={row.keys} />
    {/each}
  </Group>
</FullPage>
